import type { useAnalyticsEvents } from '@atlaskit/analytics-next';

/**
 * Shared analytics action-subject for the recommend-work-email nudge
 * (`spark_recommend_work_email_signup`). Kept as a single constant so the
 * `shown` and `emailChanged` events stay consistent and are easy to query.
 */
export const RECOMMEND_WORK_EMAIL_NUDGE_SUBJECT = 'recommendWorkEmailNudge';

type CreateAnalyticsEvent = ReturnType<typeof useAnalyticsEvents>['createAnalyticsEvent'];

/**
 * Fires the `recommendWorkEmailNudge shown` UI event.
 *
 * Emitted once, the first time the treatment nudge is surfaced to an enrolled
 * user (i.e. they have typed a confidently personal-domain email). Now that
 * experiment exposure fires automatically at page level for every enrolled
 * visitor, this event is what tells analysis which of those users actually saw
 * the nudge — i.e. those who started on a personal domain and could therefore
 * be influenced — as opposed to enrolled users who typed a business domain from
 * the outset and never saw it. Recording the domain type at nudge time makes
 * that segmentation explicit.
 */
export const fireNudgeShownAnalytics = ({
	createAnalyticsEvent,
}: {
	createAnalyticsEvent: CreateAnalyticsEvent;
}) => {
	createAnalyticsEvent({
		eventType: 'ui',
		actionSubject: RECOMMEND_WORK_EMAIL_NUDGE_SUBJECT,
		action: 'shown',
		attributes: {
			domainTypeAtNudge: 'personal-domain',
		},
	}).fire();
};

/**
 * Fires the `recommendWorkEmailNudge emailChanged` track event.
 *
 * Emitted once, when a user who was shown the nudge subsequently changes their
 * email from a personal domain to a business (non-personal) domain. This is the
 * direct signal for "how many users switched to a work email after the nudge"
 * and is the primary conversion metric for the experiment's treatment arm.
 */
export const fireNudgeEmailChangedAnalytics = ({
	createAnalyticsEvent,
}: {
	createAnalyticsEvent: CreateAnalyticsEvent;
}) => {
	createAnalyticsEvent({
		eventType: 'track',
		actionSubject: RECOMMEND_WORK_EMAIL_NUDGE_SUBJECT,
		action: 'emailChanged',
		attributes: {
			fromDomainType: 'personal-domain',
			toDomainType: 'business-domain',
		},
	}).fire();
};
